import { useBlocNext } from "@blac/react";
import styled from "@emotion/styled";
import type { FC } from "react";
import { APP_BREAKPOINT_SM } from "src/constants/layout";
import translate from "src/lib/translate";
import CareTeamDetailCubit from "src/ui/components/CareTeamSection/CareTeamDetailCubit";
import CareTeamRoleCard from "src/ui/components/CareTeamSection/CareTeamRoleCard";
import CareTeamRoleDetailSheet from "src/ui/components/CareTeamSection/CareTeamRoleDetailSheet";
import { CARE_TEAM_ROLES } from "src/ui/components/CareTeamSection/careTeamRoles";
import Translate from "src/ui/components/Translate/Translate";

/**
 * Horizontal scroll wrapper for the Care Team role cards.
 *
 * Mirrors the overflow/hidden-scrollbar pattern from
 * `AppointmentsList/components/Carousel/Carousel.tsx` and the negative-margin
 * bleed trick from `CareTeamSlider.tsx` so the list can scroll past the
 * page's horizontal padding without a CarouselCubit — v1 has no pagination or
 * programmatic scrolling.
 */
const Wrapper = styled.div`
  --edge-padding: 0rem;

  display: flex;
  gap: 0.75rem;

  overflow-x: auto;
  margin: -2.5rem calc(var(--edge-padding) * -1) -2.5rem -2.5rem;
  padding: 2.75rem calc(var(--edge-padding) + 2.5rem) 3rem 2.5rem;

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  scroll-snap-type: x proximity;
  scroll-padding-left: 2.5rem;

  @media screen and (max-width: ${APP_BREAKPOINT_SM}px) {
    gap: 0.625rem;
    --edge-padding: 2rem;
  }
`;

const CareTeamSection: FC = () => {
  const [, { open }] = useBlocNext(CareTeamDetailCubit);

  return (
    <>
      <Wrapper>
        {CARE_TEAM_ROLES.map((role) => {
          const cardTitleKey = role.cardTitleKey ?? role.titleKey;
          return (
            <CareTeamRoleCard
              key={role.id}
              title={<Translate msg={cardTitleKey} />}
              variant={role.variant}
              badge={role.badge}
              onClick={() => open(role.id)}
              ariaLabel={translate(role.titleKey)}
            />
          );
        })}
      </Wrapper>
      <CareTeamRoleDetailSheet />
    </>
  );
};

export default CareTeamSection;
